import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as moment from 'moment';
import { DropFitnessSDKService } from './dropfitnesssdk.service';
import { AuthenticationService } from './auth.service';

import { AppSettings, StorageKey } from '../appsettings';
import {
  OfferedSessionType,
  parseOfferedSessionTypes,
  parseTrainingGroupBookingPreview,
  PublicTrainerRow,
  TrainingSessionBookingPreviewResponse
} from '../models/pt-booking.models';
import { BookingSessionType } from '../models/trainer.models';

@Injectable({
  providedIn: 'root'
})
export class TrainerService {
  /**
   * Member/PTSearch page size — smaller payloads feel faster on slow networks; raise if trainers need more in one view.
   * Backend: ensure `Member/PTSearch` uses indexed TrainerId + name search (see facility API / df-api-services).
   */
  private static readonly PT_SEARCH_PAGE_SIZE = 50;

  constructor(private http: HttpClient, private dfService: DropFitnessSDKService, private authService: AuthenticationService) {
  }

  /**
   * Get trainer info by personId
   * Return name, trainerId, other relevant trainer info
   * @param {number} personId
   */
  getInfo = () => {
    return new Promise((resolve, reject) => {
      this.http.get<any>(`${this.dfService.versionedFacilityUrl()}Employee/Trainer/Details`)
        .subscribe((trainer: any) => {
          if (trainer && trainer.Id) {
            trainer.Avatar.UploadedUrl = trainer.Avatar?.UploadedUrl.length != 0 ? trainer.Avatar?.UploadedUrl : "assets/images/users/user-dummy-img.jpg";
            localStorage.setItem(StorageKey.CURRENT_USER, JSON.stringify(trainer));
            var avatar = trainer.Avatar?.UploadedUrl.length != 0 ? trainer.Avatar?.UploadedUrl : "assets/images/users/user-dummy-img.jpg"
            localStorage.setItem(StorageKey.CURRENT_USER_AVATAR, avatar);
            this.authService.setUserAvatar(avatar);
            resolve(trainer);
          } else {
            reject(this._handleError(trainer || {message: 'Trainer could not be fetched.'}));
          }
          return trainer;
        },
        (error: any) => {
          reject(this._handleError(error));
        });
    });
  }

  /**
   * POST Member/PTSearch — Observable so callers can `switchMap` and cancel superseded requests.
   */
  searchPtClients$(_trainerId: number, searchTerm: string = ''): Observable<any[]> {
    const body: Record<string, unknown> = {
      SearchTerm: searchTerm ?? '',
      Page: 1,
      PageSize: TrainerService.PT_SEARCH_PAGE_SIZE,
      Units: [1]
    };
    if (_trainerId != null && _trainerId > 0) {
      body['TrainerId'] = _trainerId;
    }
    return this.http.post<any>(`${this.dfService.versionedFacilityUrl()}Member/PTSearch`, body).pipe(
      map((response: any) => {
        const rawList = response?.Records ?? response?.records;
        return Array.isArray(rawList) ? rawList : [];
      })
    );
  }

  /**
   * Get trainer clients via Member/PTSearch (same API as df-frontdesk).
   * Each client contains: name, photo, scheduleId, currencyBalances, etc.
   * @param _trainerId Facility-wide search: omit TrainerId when 0 — matches df-frontdesk.
   * @param searchTerm Optional name search (debounced in UI; matches frontdesk SelectTrainerMember).
   */
  getClients = (_trainerId: number, searchTerm: string = '') => {
    return new Promise((resolve, reject) => {
      this.searchPtClients$(_trainerId, searchTerm).subscribe(
        (clients) => resolve(clients),
        (error: any) => reject(this._handleError(error))
      );
    });
  }

  /**
   * Get trainer  sessions by trainerId. 30 days of past sessions + all future.
   * Returns array of sessions
   * @param {number} trainerId
   */
  getSessions = (trainerId: number) => {
    return new Promise((resolve, reject) => {
      this.http.get<any>(`${this.dfService.versionedFacilityUrl()}Training/Booking/ByTrainer/${trainerId}`)
        .subscribe((sesisons: any) => {
          if (sesisons && !(sesisons.length == undefined)) {
            resolve(sesisons);
          } else {
            reject(this._handleError(sesisons));
          }
          return sesisons;
        },
        (error: any) => {
          reject(this._handleError(error));
        });
    });
  }

  /**
   * Get past trainer  sessions by trainerId. 30 days of past sessions.
   * Returns array of sessions
   * @param {number} trainerId
   */
  getPastSessions = (trainerId: number) => {
    return this.getSessionsFeed(trainerId).then(
      (x: any) => x?.Past ?? x?.past ?? []
    );
  }

  /**
   * Get upcoming trainer  sessions by trainerId. 30 days of past sessions.
   * Returns array of sessions
   * @param {number} trainerId
   */
  getUpcomingSessions = (trainerId: number) => {
    return this.getSessionsFeed(trainerId).then((x: any) => x?.Upcoming ?? x?.upcoming ?? []);
  }

  getOfferedSessionTypes = (): Promise<OfferedSessionType[]> => {
    return new Promise((resolve) => {
      this.http.get<any>(`${this.dfService.versionedFacilityUrl()}Training/Sessions/OfferedTypes`)
        .subscribe({
          next: (payload) => resolve(parseOfferedSessionTypes(payload)),
          error: () => resolve([])
        });
    });
  }

  getSessionsFeed = (trainerId: number) => {
    return new Promise((resolve, reject) => {
      this.http.get<any>(`${this.dfService.versionedFacilityUrl()}Training/Sessions/Feed/${trainerId}`)
        .subscribe({
          next: (payload) => resolve(payload),
          error: (error: any) => reject(this._handleError(error))
        });
    });
  }

  /**
   * Get trainer statistics by trainerId.
   * Returns trainer stats.
   * @param {number} trainerId
   */
  /**
   * Active trainers for Add session dropdown (same source as df-frontdesk).
   * GET Meta/TrainingTab/Public → Trainers; filter id !== 0, state === 1; sort by first name.
   */
  getPublicTrainers = (): Promise<PublicTrainerRow[]> => {
    return new Promise((resolve, reject) => {
      this.http.get<any>(`${this.dfService.versionedFacilityUrl()}Meta/TrainingTab/Public`)
        .subscribe({
          next: (res: any) => {
            const raw = res?.Trainers ?? res?.trainers;
            const list = Array.isArray(raw) ? raw : [];
            const rows: PublicTrainerRow[] = list
              .map((t: any) => ({
                Id: t?.Id ?? t?.id ?? 0,
                State: t?.State ?? t?.state ?? 0,
                Person: t?.Person ?? t?.person
              }))
              .filter((t: PublicTrainerRow) => t.Id !== 0 && t.State === 1);
            rows.sort((a, b) => {
              const fa = (a.Person?.FirstName ?? '').toLowerCase();
              const fb = (b.Person?.FirstName ?? '').toLowerCase();
              return fa.localeCompare(fb);
            });
            resolve(rows);
          },
          error: (error: any) => {
            reject(this._handleError(error));
          }
        });
    });
  };

  /**
   * POST Training/Booking/Preview — camelCase JSON body.
   */
  previewPtBooking = (body: Record<string, unknown>) => {
    return new Promise<{
      httpOk: boolean;
      transportError?: string;
      preview: TrainingSessionBookingPreviewResponse | null;
    }>((resolve) => {
      this.http.post<any>(`${this.dfService.versionedFacilityUrl()}Training/Sessions/Preview`, body)
        .subscribe({
          next: (data: any) => {
            resolve({
              httpOk: true,
              preview: parseTrainingGroupBookingPreview(data)
            });
          },
          error: (err: any) => {
            const payload = err?.error;
            const preview = parseTrainingGroupBookingPreview(payload);
            const msg = this._errorsFromPreviewBody(payload, err?.message);
            resolve({
              httpOk: false,
              transportError: msg,
              preview
            });
          }
        });
    });
  };

  /**
   * POST Training/Booking/Confirm — same body as Preview.
   */
  confirmPtBooking = (body: Record<string, unknown>) => {
    return new Promise<{
      httpOk: boolean;
      transportError?: string;
      preview: TrainingSessionBookingPreviewResponse | null;
    }>((resolve) => {
      this.http.post<any>(`${this.dfService.versionedFacilityUrl()}Training/Sessions/Confirm`, body)
        .subscribe({
          next: () => {
            resolve({ httpOk: true, preview: null, transportError: undefined });
          },
          error: (err: any) => {
            const payload = err?.error;
            const preview = parseTrainingGroupBookingPreview(payload);
            resolve({
              httpOk: false,
              transportError: this._errorsFromPreviewBody(payload, err?.message),
              preview
            });
          }
        });
    });
  };

  private _errorsFromPreviewBody(payload: any, fallback: string | undefined): string {
    if (payload && typeof payload === 'object') {
      const errs = payload.errors ?? payload.Errors;
      if (Array.isArray(errs) && errs.length) {
        return errs.map((e: any) => String(e)).filter(Boolean).join('; ');
      }
    }
    return fallback || 'Request failed';
  }

  getStats = (trainerId: number) => {
    return new Promise((resolve, reject) => {
      this.http.get<any>(`${this.dfService.versionedFacilityUrl()}Employee/Trainer/Stats/ByTrainer/${trainerId}`)
        .subscribe((stats: any) => {
          if (stats) {
            resolve(stats);
          } else {
            reject(this._handleError(stats));
          }
          return stats;
        },
        (error: any) => {
          reject(this._handleError(error));
        });
    });
  }

  scheduleSession(trainerId: number, session: any) {
    let promises: Array<Promise<any>> = [];
    session.clients.forEach((client: {
      id: number;
      scheduleId: number;
    }) => {
      promises.push(new Promise((resolve, reject) => {
        this.http.post(`${this.dfService.versionedFacilityUrl()}Training/Booking/`, {
          ScheduleId: client.scheduleId,
          TrainerId: trainerId,
          MemberId: client.id,
          StartDate: session.sessionDate,
          StartTime: `0.${session.sessionTime}.0`,
          Type: session.sessionType,
          Duration: session.duration
        })
          .subscribe((sessions: any) => {
            if (sessions && !(sessions.length === undefined)) {
              resolve(sessions);
            } else {
              reject(this._handleError(sessions));
            }
            return sessions;
          },
          (error: any) => {
            reject(this._handleError(error));
          });
      }));
    });
    return Promise.all(promises).catch(() => {

    });
  }

  scheduleRecurringSessions(trainerId: number, dates: Date[], session: any) {
    let promises: Array<Promise<any>> = [];
    session.clients.forEach((client: {
      id: number;
      scheduleId: number;
    }) => {
      let multiBookingReq: {
        ScheduleId: number,
        TrainerId: number,
        MemberId: number,
        Type: any,
        Duration: any,
        Timings: any[];
      } = {
        ScheduleId: client.scheduleId,
        TrainerId: trainerId,
        MemberId: client.id,
        Type: session.sessionType,
        Duration: session.duration,
        Timings: []
      };
      dates.forEach(date => {
        multiBookingReq.Timings.push({
          StartDate: date,
          StartTime: `0.${session.sessionTime}.0`,
        });
      });
      promises.push(new Promise((resolve, reject) => {
        this.http.post(`${this.dfService.versionedFacilityUrl()}Training/Booking/MultiBooking/`, multiBookingReq)
          .subscribe((response: any) => {
            if (response && !(response.length == undefined)) {
              resolve(response);
            } else {
              reject(this._handleError(response));
            }
            return response;
          },
          (error: any) => {
            reject(this._handleError(error));
          });
      }));
    });
    
    return Promise.all(promises).catch(() => {

    });
  }

  /**
   * Single booking row (private or slot anchor). For slot sessions the API embeds
   * `SlotSession` (or returns the slot shape directly in Training/Sessions detail).
   */
  getBookingById(bookingId: number): Promise<Record<string, unknown>> {
    return new Promise((resolve, reject) => {
        this.http
        .get<Record<string, unknown>>(
          `${this.dfService.versionedFacilityUrl()}Training/Sessions/${bookingId}`
        )
        .subscribe({
          next: (data) => resolve(data ?? {}),
          error: (err) => reject(this._handleError(err))
        });
    });
  }

  /**
   * Slot attendees + balances. Uses only `GET Training/Sessions/{id}` and reads
   * `SlotSession` / `slotSession` from the response if present.
   */
  getGroupSessionDetail(bookingId: number): Promise<Record<string, unknown>> {
    return this.getBookingById(bookingId).then((booking) => {
      // New Training/Sessions detail returns slot shape directly (`Attendees`),
      // while older code paths still read `Members`.
      if (booking && typeof booking === 'object') {
        const attendees = booking['Attendees'] ?? booking['attendees'];
        if (Array.isArray(attendees)) {
          return {
            ...booking,
            Members: attendees,
            members: attendees,
            IsGroupSession:
              booking['IsSlotSession'] ??
              booking['isSlotSession'] ??
              booking['IsGroupSession'] ??
              booking['isGroupSession'] ??
              false
          };
        }
      }
      const gs =
        (booking['SlotSession'] ??
          booking['slotSession'] ??
          booking['GroupSession'] ??
          booking['groupSession']) as Record<string, unknown> | undefined;
      if (gs && typeof gs === 'object') {
        const attendees = gs['Attendees'] ?? gs['attendees'];
        if (Array.isArray(attendees)) {
          return {
            ...gs,
            Members: attendees,
            members: attendees,
            IsGroupSession:
              gs['IsSlotSession'] ??
              gs['isSlotSession'] ??
              gs['IsGroupSession'] ??
              gs['isGroupSession'] ??
              true
          };
        }
        return gs;
      }
      return {
        Members: [],
        members: [],
        IsGroupSession: false,
        isGroupSession: false
      };
    });
  }

  updateGroupSession(payload: { BookingId: number; StartDate: string; StartTime: string; TrainerId?: number }) {
    return new Promise((resolve, reject) => {
      this.http.put(`${this.dfService.versionedFacilityUrl()}Training/Sessions/${payload.BookingId}`, payload)
        .subscribe({
          next: (res: any) => resolve(res),
          error: (err: any) => reject(this._handleError(err))
        });
    });
  }

  updateSession(session: any) {
    const isGroup = session.sessionType === BookingSessionType.GroupPT || session.sessionType === 2;
    if (isGroup) {
      return new Promise((resolve, reject) => {
        this.http.put(`${this.dfService.versionedFacilityUrl()}Training/Sessions/${session.id}`, {
          BookingId: session.id,
          StartDate: moment(new Date(session.sessionDate)).format('YYYY-MM-DD'),
          StartTime: session.sessionTime
        })
          .subscribe({
            next: (res: any) => resolve(res),
            error: (err: any) => reject(this._handleError(err))
          });
      });
    }
    return new Promise((resolve, reject) => this.http.put(`${this.dfService.versionedFacilityUrl()}Training/Sessions/${session.id}`, {
      BookingId: session.id,
      StartDate: moment(new Date(session.sessionDate)).format('YYYY-MM-DD'),
      StartTime: session.sessionTime
    })
      .subscribe((sesison: any) => {
        if (sesison && sesison.Id) {
          resolve(sesison);
        } else {
          reject(this._handleError(sesison));
        }
        return sesison;
      },
        (error: any) => {
          reject(this._handleError(error));
        }));
  }

  cancelSession(bookingId: number, refund: boolean) {
    return new Promise((resolve, reject) => {
      this.http.delete(`${this.dfService.versionedFacilityUrl()}Training/Sessions/${bookingId}/${refund}`)
        .subscribe(() => {
            resolve(true);
          },
          (error) => {
            reject(error)
          }
        );
    });
  }

  /** Cancels all active bookings in the same group PT slot as the anchor booking id. */
  cancelGroupSession(anchorBookingId: number, refund: boolean) {
    return new Promise((resolve, reject) => {
      this.http.delete(`${this.dfService.versionedFacilityUrl()}Training/Sessions/${anchorBookingId}/${refund}`)
        .subscribe({
          next: (res) => resolve(res),
          error: (err) => reject(this._handleError(err))
        });
    });
  }

  cancelSessionsBatch(bookingIds: number[], refund: boolean) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.dfService.versionedFacilityUrl()}Training/Sessions/CancelBatch`, {
        bookingIds,
        refund
      }).subscribe({
        next: (res) => resolve(res),
        error: (err) => reject(this._handleError(err))
      });
    });
  }

  /**
   * Handle the error
   * @param {*} error
   */
  _handleError(error: any) {
      // tslint:disable-next-line: prefer-const
      var errorMessage = error.message;
      return errorMessage;
  }
}
